import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { BRAND_INFO } from '../data/mock';
import { Layers, Shield, Sparkles, CheckCircle, ArrowRight } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const MaterialPage = () => {
  usePageTitle('Quality Materials', 'Learn about the premium materials used in Stitch Your Dash sewing tools and craft supplies.');

  const materials = [
    {
      name: 'Stainless Steel',
      description: 'Our cutting tools and precision instruments are crafted from high-grade stainless steel for durability and rust resistance.',
      benefits: ['Rust-resistant', 'Long-lasting sharpness', 'Easy to clean', 'Professional grade']
    },
    {
      name: 'Premium Plastics',
      description: 'We use BPA-free, impact-resistant plastics for ergonomic handles and durable components.',
      benefits: ['Lightweight', 'Comfortable grip', 'Impact-resistant', 'Eco-friendly options']
    },
    {
      name: 'Quality Fabrics',
      description: 'Our dress forms and fabric-related products use premium materials for accurate fitting and longevity.',
      benefits: ['Pinnable surfaces', 'True-to-size fitting', 'Durable construction', 'Professional results']
    },
    {
      name: 'LED Technology',
      description: 'Our lighting products feature energy-efficient LED technology with natural daylight simulation.',
      benefits: ['Energy efficient', 'Low heat output', 'Long lifespan', 'True color rendering']
    }
  ];

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Hero with Banner Image */}
        <section className="relative bg-warmBrown-800 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1542044801-30d3e45ae49a?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-900 via-warmBrown-900/90 to-warmBrown-800/70" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-20" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-2xl">
              <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-6">
                <Layers className="w-8 h-8 text-rose-400" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4">Quality Materials</h1>
              <p className="text-warmBrown-300 text-lg">We source only the finest materials to ensure our products meet the highest standards of quality and durability.</p>
            </div>
          </div>
        </section>

        {/* Materials Grid */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {materials.map((material, index) => (
                <div key={index} className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
                  <h3 className="font-display text-2xl text-warmBrown-900 mb-4">{material.name}</h3>
                  <p className="text-warmBrown-600 mb-6 leading-relaxed">{material.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {material.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                        <span className="text-warmBrown-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality Promise */}
        <section className="py-16 lg:py-20 bg-cream-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Shield className="w-12 h-12 text-rose-500 mx-auto mb-6" />
            <h2 className="font-display text-3xl text-warmBrown-900 mb-6">Our Quality Promise</h2>
            <p className="text-warmBrown-600 text-lg leading-relaxed mb-8">
              At {BRAND_INFO.name}, we believe that quality materials lead to quality results. Every product in our catalog is carefully selected to ensure it meets our strict standards for durability, functionality, and value.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20 group"
            >
              Shop Our Products
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MaterialPage;
